<template>
  <Sidebar
    v-model:visible="internalVisible"
    position="right"
    class="integration-details-sidebar"
    :modal="true"
    :dismissable="true"
  >
    <template #header>
      <span class="sidebar-header-title">
        {{ t('integrations.details.title') }}
      </span>
    </template>

    <div class="sidebar-container">
      <!-- Loading -->
      <div
        v-if="loading"
        class="sidebar-loading"
      >
        <Loader />
      </div>

      <!-- Content -->
      <div
        v-else-if="integration"
        class="sidebar-content"
      >
        <!-- Integration identity -->
        <div class="detail-identity">
          <div
            class="detail-icon"
            :class="{ 'has-image': integration.icon_url }"
          >
            <img
              v-if="integration.icon_url"
              :src="integration.icon_url"
              :alt="localizedName"
            >
            <i
              v-else
              class="fas fa-puzzle-piece"
            />
          </div>
          <div class="detail-meta">
            <h2 class="detail-name">
              {{ localizedName }}
            </h2>
            <p
              v-if="partnerName"
              class="detail-partner"
            >
              {{ partnerName }}
            </p>
            <span
              v-if="integration.version"
              class="detail-version"
            >
              v{{ integration.version }}
            </span>
            <span
              v-if="isRestricted"
              class="detail-restricted"
            >
              <i class="fas fa-lock" />
              {{ t('integrations.visibility.restricted') }}
            </span>
          </div>
        </div>

        <!-- Portée restreinte : explication lisible sans survol (mobile compris). -->
        <p
          v-if="isRestricted"
          class="detail-restricted-hint"
        >
          {{ t('integrations.visibility.restrictedHint') }}
        </p>

        <!-- Status (if installed) -->
        <div
          v-if="currentStatus && currentStatus !== 'uninstalled'"
          class="detail-section"
        >
          <div class="detail-status-row">
            <span
              class="detail-status-badge"
              :class="`detail-status-badge--${currentStatus}`"
            >
              <i :class="statusIcon" />
              {{ statusLabel }}
            </span>
            <span
              v-if="currentStatus === 'active' || currentStatus === 'inactive'"
              :title="activationLocked ? t('integrations.activation.configureFirst') : ''"
            >
              <InputSwitch
                :model-value="currentStatus === 'active'"
                :disabled="activationLocked"
                @update:model-value="$emit('toggle-status', integration)"
              />
            </span>
          </div>
        </div>

        <!-- Short description -->
        <div
          v-if="localizedShortDescription"
          class="detail-section"
        >
          <h3 class="detail-section-title">
            {{ t('integrations.details.description') }}
          </h3>
          <p class="detail-description">
            {{ fullDescription || localizedShortDescription }}
          </p>
        </div>

        <!-- Categories -->
        <div
          v-if="integration.integration_categories_data && integration.integration_categories_data.length > 0"
          class="detail-section"
        >
          <h3 class="detail-section-title">
            {{ t('integrations.filters.categories') }}
          </h3>
          <div class="detail-categories">
            <span
              v-for="(category, index) in integration.integration_categories_data"
              :key="category.id_category || index"
              class="detail-category-chip"
            >
              {{ getCategoryName(category) }}
            </span>
          </div>
        </div>

        <!-- Documentation -->
        <div
          v-if="documentationUrl"
          class="detail-section"
        >
          <a
            :href="documentationUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="detail-doc-link"
          >
            <i class="fas fa-book" />
            {{ t('integrations.details.viewDocumentation') }}
            <i class="fas fa-external-link-alt detail-doc-link-ext" />
          </a>
        </div>
      </div>

      <!-- Footer actions -->
      <div
        v-if="integration && !loading"
        class="sidebar-footer"
      >
        <!-- Coming soon: disabled CTA, install not allowed yet -->
        <template v-if="isComingSoon">
          <SpmButton
            :label="t('integrations.actions.comingSoon')"
            icon="pi pi-clock"
            class-style="p-button p-button-secondary"
            :disabled="true"
          />
        </template>

        <!-- Not installed: Install button -->
        <template v-else-if="!currentStatus || currentStatus === 'uninstalled'">
          <SpmButton
            :label="t('integrations.actions.install')"
            icon="pi pi-download"
            class-style="p-button p-button-success"
            @click="$emit('install', integration)"
          />
        </template>

        <!-- Pending auth: Cancel -->
        <template v-else-if="currentStatus === 'pending_auth'">
          <SpmButton
            :label="t('integrations.actions.cancelInstallation')"
            icon="pi pi-times"
            class-style="p-button p-button-danger p-button-outlined"
            @click="$emit('cancel-installation', integration)"
          />
        </template>

        <!-- Installed: Configure + Uninstall -->
        <template v-else>
          <SpmButton
            v-if="hasConfig"
            :label="t('integrations.actions.configure')"
            icon="pi pi-cog"
            class-style="p-button p-button-success"
            @click="$emit('configure', integration)"
          />
          <SpmButton
            :label="t('integrations.actions.uninstall')"
            icon="pi pi-trash"
            class-style="p-button p-button-danger p-button-outlined"
            @click="$emit('uninstall', integration)"
          />
        </template>
      </div>
    </div>
  </Sidebar>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch,
} from 'vue';
import type { PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import Sidebar from 'primevue/sidebar';
import InputSwitch from 'primevue/inputswitch';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import Loader from '@/components/layout/Loader.vue';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import type { SpmApiFilter } from '@/components/table-rest-api/filters';
import type { Integration } from '@/composables/useIntegrations';
import { pickCategoryName, pickLocalizedText } from '@/utils/localized-text';

interface CategoryData {
  id_category?: string | number;
  category_name: string;
}

export default defineComponent({
  name: 'IntegrationDetailsSidebar',

  components: {
    Sidebar,
    InputSwitch,
    SpmButton,
    Loader,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    integration: {
      type: Object as PropType<Integration | null>,
      default: null,
    },
    shopIntegrationStatus: {
      type: String,
      default: '',
    },
  },

  emits: [
    'update:visible',
    'install',
    'uninstall',
    'toggle-status',
    'configure',
    'cancel-installation',
  ],

  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const loading = ref(false);
    const fullDescription = ref('');
    const documentationUrl = ref('');

    const internalVisible = computed({
      get: () => props.visible,
      set: (value: boolean) => {
        emit('update:visible', value);
      },
    });

    const partnerName = computed(() => {
      if (!props.integration?.partners_data || props.integration.partners_data.length === 0) {
        return '';
      }
      return props.integration.partners_data[0].partner_name || '';
    });

    // G14 — nom / description courte localisés (C10) avec repli sur les champs
    // string legacy. Tolérant à l'absence des champs `*_i18n`.
    const localizedName = computed(() => pickLocalizedText(
      (props.integration as any)?.name_i18n,
      locale.value,
    ) || props.integration?.name || '');

    const localizedShortDescription = computed(() => pickLocalizedText(
      (props.integration as any)?.short_description_i18n,
      locale.value,
    ) || props.integration?.short_description || '');

    const currentStatus = computed(() => props.shopIntegrationStatus || '');

    const isComingSoon = computed(() => props.integration?.status === 'soon');

    // Portée par boutique : intégration réservée par ShopiMind à une sélection de boutiques.
    const isRestricted = computed(() => props.integration?.visibility === 'restricted');

    const hasConfig = computed(() => !!(
      (props.integration?.config?.fields && props.integration.config.fields.length > 0)
      || (props.integration?.config?.steps && props.integration.config.steps.length > 0)
    ));

    // T1 — an installed-but-unconfigured integration (config schema present but
    // not completed / connection not verified) must not be activatable: the
    // toggle is locked until `configured`. Deactivating an active one stays free.
    const isConfigured = computed(() => !!props.integration?.configured);
    const activationLocked = computed(() => currentStatus.value === 'inactive'
      && hasConfig.value && !isConfigured.value);

    const statusIcon = computed(() => {
      switch (currentStatus.value) {
        case 'active': return 'fas fa-check-circle';
        case 'inactive': return 'fas fa-pause-circle';
        case 'pending_auth': return 'fas fa-clock';
        default: return 'fas fa-circle';
      }
    });

    const statusLabel = computed(() => {
      switch (currentStatus.value) {
        case 'active': return t('integrations.status.active');
        case 'inactive': return t('integrations.status.inactive');
        case 'pending_auth': return t('integrations.status.pendingAuth');
        default: return '';
      }
    });

    // Parseur `category_name` + sélection localisée centralisés (G12).
    const getCategoryName = (category: CategoryData): string => pickCategoryName(category.category_name, locale.value);

    const fetchFullDetails = async () => {
      if (!props.integration?.slug) return;

      loading.value = true;
      try {
        const filters: SpmApiFilter[] = [
          {
            field: 'slug',
            operator: SpmFilterOperator.EQUALS,
            values: props.integration.slug,
          },
        ];

        const fields = [
          'description',
          'description_i18n',
          'documentation_url',
        ].join(',');

        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          '/integrations',
          {},
          '',
          undefined,
          true,
          { limit: 1, offset: 0, fields, filters },
        );

        if (response?.data && response.data.length > 0) {
          const detail = response.data[0];
          // G14 — préfère la description localisée (C10) si présente, repli legacy.
          fullDescription.value = pickLocalizedText(detail.description_i18n, locale.value)
            || detail.description || '';
          documentationUrl.value = detail.documentation_url || '';
        }
      } catch (error) {
        console.error('Error fetching integration details:', error);
      } finally {
        loading.value = false;
      }
    };

    // Fetch details when sidebar opens
    watch(() => props.visible, (isVisible) => {
      if (isVisible && props.integration) {
        fullDescription.value = '';
        documentationUrl.value = '';
        fetchFullDetails();
      }
    });

    return {
      t,
      internalVisible,
      loading,
      fullDescription,
      documentationUrl,
      partnerName,
      currentStatus,
      isComingSoon,
      isRestricted,
      hasConfig,
      activationLocked,
      statusIcon,
      statusLabel,
      getCategoryName,
      localizedName,
      localizedShortDescription,
    };
  },
});
</script>

<style lang="scss">
.integration-details-sidebar {
  width: 420px !important;

  .p-sidebar-header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;

    .p-sidebar-header-content {
      width: 100%;
    }
  }

  .p-sidebar-content {
    padding: 0 !important;
    height: 100%;
  }
}

.sidebar-header-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
}

.sidebar-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 3rem;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

/* Identity */
.detail-identity {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.detail-icon {
  flex-shrink: 0;
  width: 3.5rem;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #94c840 0%, #7fb32e 100%);
  border-radius: 0.75rem;
  color: #ffffff;
  font-size: 1.5rem;
  overflow: hidden;

  &.has-image {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    padding: 0.25rem;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 0.625rem;
  }
}

.detail-meta {
  flex: 1;
  min-width: 0;
}

.detail-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.25rem;
  line-height: 1.3;
}

.detail-partner {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0 0 0.375rem;
}

.detail-version {
  display: inline-block;
  font-family: monospace;
  font-size: 0.75rem;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
}

.detail-restricted {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: 0.375rem;
  padding: 0.125rem 0.5rem;
  background: #f3e8ff;
  color: #6b21a8;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
  vertical-align: middle;

  i {
    font-size: 0.5625rem;
  }
}

.detail-restricted-hint {
  margin: 0.75rem 0 0;
  padding: 0.5rem 0.75rem;
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  border-radius: 6px;
  color: #6b21a8;
  font-size: 0.8125rem;
  line-height: 1.4;
}

/* Sections */
.detail-section {
  padding: 1rem 0;
  border-top: 1px solid #f3f4f6;
}

.detail-section-title {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  margin: 0 0 0.625rem;
}

/* Status */
.detail-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.detail-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 600;

  i {
    font-size: 0.75rem;
  }

  &--active {
    background: rgba(148, 200, 64, 0.15);
    color: #4d7c0f;
  }

  &--inactive {
    background: #f3f4f6;
    color: #6b7280;
  }

  &--pending_auth {
    background: #fef3c7;
    color: #b45309;
  }
}

/* Description */
.detail-description {
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.7;
  margin: 0;
}

/* Categories */
.detail-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.detail-category-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

/* Documentation */
.detail-doc-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  text-decoration: none;
  font-size: 0.8125rem;
  font-weight: 500;
  transition: all 0.15s ease;
  width: 100%;

  &:hover {
    border-color: #94c840;
    color: #94c840;
    background: rgba(148, 200, 64, 0.1);
  }

  i {
    font-size: 0.875rem;
    color: #9ca3af;
  }

  .detail-doc-link-ext {
    margin-left: auto;
    font-size: 0.6875rem;
  }
}

/* Footer */
.sidebar-footer {
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;

  .p-button {
    flex: 1;
  }
}
</style>
